import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { PERM } from '../models/permission-codes';
import { AssignmentService } from '../../../features/assistant/services/assignment.service';
import { CurrentEmployeeService } from './current-employee.service';
import { PermissionService } from './permission.service';

/**
 * Les clients qu'un utilisateur a le droit de voir dans un sélecteur.
 *
 * Avec « Tous les clients » (client_all), aucun filtre. Sinon, l'assistant ne voit que les clients
 * qui lui sont affectés — et aucun s'il n'a pas de fiche employé.
 */
@Injectable({ providedIn: 'root' })
export class VisibleClientsService {
  private readonly permissionService = inject(PermissionService);
  private readonly currentEmployee = inject(CurrentEmployeeService);
  private readonly assignmentService = inject(AssignmentService);

  /** Garde de `items` ceux que l'utilisateur peut voir. */
  filter<T extends { id?: string | number | null }>(items: readonly T[]): Observable<T[]> {
    if (this.permissionService.can(PERM.CLIENT_ALL_READ)) {
      return of([...items]);
    }
    return this.assignedClientIds().pipe(
      map((assigned) => items.filter((item) => item.id != null && assigned.has(String(item.id))))
    );
  }

  private assignedClientIds(): Observable<Set<string>> {
    return this.currentEmployee.current().pipe(
      switchMap((me) => (me ? this.assignmentService.getAssignedClients(me.id) : of([]))),
      map((assigned) => new Set(assigned.map((client) => String(client.organizationId)))),
      catchError(() => of(new Set<string>()))
    );
  }
}
